import React from 'react';
import moment from 'moment';
import { Form, FormGroup, DatePicker, ValidationMessage } from '~/components/Form';
import { Select } from '~/components/Select';
import { BannerType, ServicesContext, useAdminService } from '~/services';
import { enumsToOptions, enumValue } from '~/util';
import { BannerStyleOption } from '.';

const styles = enumsToOptions(['banners', 'style']);
const types = enumsToOptions(['banners', 'type']);
const triggers = enumsToOptions(['banners', 'triggers']);

interface StyleOptionType {
    value: number;
    label: string;
}

export interface BannerFormProps {
    banner?: Partial<BannerType>;
    saving: boolean;
    setSaving: (x: boolean) => void;
    formId: string;
    setValid: (x: boolean) => void;
    setShow: (x: boolean) => void;
}
export function BannerForm({
    banner = {},
    saving,
    setSaving,
    formId,
    setValid,
    setShow,
}: BannerFormProps) {
    const context = React.useContext(ServicesContext);
    const { storeBanner } = useAdminService();
    const [content, setContent] = React.useState(banner?.content || '');
    const [style, setStyle] = React.useState<undefined | StyleOptionType>(
        banner?.style ? styles.find((x) => x.value === banner.style) : styles[0]
    );
    const [bannerType, setBannerType] = React.useState(
        banner?.settings?.type
            ? types.find((x) => x.value === banner.settings?.type)
            : types.find((x) => x.value === enumValue(['banners', 'type', 'Banner']))
    );
    const [bannerTrigger, setBannerTrigger] = React.useState(
        banner?.settings?.trigger
            ? triggers.find((x) => x.value === banner.settings?.trigger)
            : null
    );
    const [bannerHeader, setBannerHeader] = React.useState(banner?.settings?.header ?? '');
    const [startDate, setStartDate] = React.useState(
        banner?.start_date ? moment(banner.start_date).toDate() : null
    );
    const [endDate, setEndDate] = React.useState(
        banner?.end_date ? moment(banner.end_date).toDate() : null
    );
    const [invalidDates, setInvalidDates] = React.useState(false);
    const [responseMessage, setResponseMessage] = React.useState(false);
    const [validation, setValidation] = React.useState(false);

    async function handleSubmit() {
        try {
            setSaving(true);
            await storeBanner(banner?.id, {
                content,
                style: style?.value,
                settings: {
                    type: bannerType?.value,
                    header: bannerHeader,
                    trigger: bannerTrigger?.value,
                },
                start_date: startDate!,
                end_date: endDate!,
            });
            context.alert.set('Banner created successfully', 'success');
            setSaving(false);
            setShow(false);
        } catch (e) {
            setSaving(false);
            setValidation(true);
            setResponseMessage(e.response);
        }
    }

    React.useEffect(() => {
        if (startDate !== null && endDate !== null) {
            if (moment(endDate).isBefore(moment(startDate), 'day')) {
                setInvalidDates(true);
                setValid(false);
                return;
            }
            setInvalidDates(false);
        }
        if (content.trim().length > 0 && style !== null && startDate !== null && endDate !== null) {
            setValid(true);
        } else {
            setValid(false);
        }
    }, [content, endDate, setValid, startDate, style]);

    const minDate = banner?.start_date ? moment(banner.start_date).toDate() : new Date();

    return (
        <Form
            id={formId}
            disabled={saving}
            onSubmit={handleSubmit}
            values={{ content, style, startDate, endDate }}
        >
            <div className="row">
                <FormGroup htmlFor="banner-content" className="col-12">
                    <label htmlFor="banner-content" className="col-form-label">
                        Banner Message
                    </label>
                    <textarea
                        id="banner-content"
                        className="form-control"
                        rows={6}
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                    />
                    <ValidationMessage
                        className="col-12 text-center mt-2"
                        rule={validation}
                        type="external"
                        showBeforeSubmit
                    >
                        {Array.isArray(responseMessage)
                            ? responseMessage.map((x) => <div key={x.message}>{x.message}</div>)
                            : responseMessage}
                    </ValidationMessage>
                </FormGroup>
            </div>
            <div className="row">
                <FormGroup htmlFor="banner-type" className="col-6">
                    <label htmlFor="banner-type" className="col-form-label">
                        Type
                    </label>
                    <Select
                        id="banner-type"
                        label="Type"
                        options={types}
                        value={bannerType}
                        onChange={setBannerType}
                    />
                </FormGroup>
                {bannerType?.value === enumValue(['banners', 'type', 'Modal']) ? (
                    <FormGroup htmlFor="banner-header" className="col-6">
                        <label htmlFor="banner-header" className="col-form-label">
                            Header
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            id="banner-header"
                            value={bannerHeader}
                            onChange={(e) => setBannerHeader(e.target.value)}
                        />
                    </FormGroup>
                ) : (
                    <FormGroup htmlFor="banner-style" className="col-6">
                        <label htmlFor="banner-style" className="col-form-label">
                            Style
                        </label>
                        <Select
                            components={{ Option: BannerStyleOption }}
                            id="banner-style"
                            label="Style"
                            options={styles}
                            value={style}
                            onChange={(val: StyleOptionType) => setStyle(val)}
                        />
                    </FormGroup>
                )}
            </div>
            <div className="row">
                <FormGroup htmlFor="banner-trigger" className="col-6">
                    <label htmlFor="banner-trigger" className="col-form-label">
                        Trigger
                    </label>
                    <Select
                        id="banner-trigger"
                        label="Trigger"
                        options={triggers}
                        value={bannerTrigger}
                        onChange={setBannerTrigger}
                        isClearable
                    />
                </FormGroup>
            </div>
            <div className="row">
                <FormGroup htmlFor="start-date" className="col-6">
                    <label htmlFor="start-date" className="col-form-label">
                        Start Date
                    </label>
                    <div>
                        <DatePicker
                            id="start-date"
                            name="start-date"
                            value={startDate}
                            onChange={(val: null | Date) => setStartDate(val)}
                            minDate={minDate}
                        />
                    </div>
                </FormGroup>
                <FormGroup htmlFor="end-date" className="col-6">
                    <label htmlFor="end-date" className="col-form-label">
                        End Date
                    </label>
                    <div>
                        <DatePicker
                            id="end-date"
                            name="end-date"
                            value={endDate}
                            onChange={(val: null | Date) => setEndDate(val)}
                            minDate={minDate}
                        />
                    </div>
                    <ValidationMessage type="external" rule={invalidDates} showBeforeSubmit>
                        End Date must be after start date
                    </ValidationMessage>
                </FormGroup>
            </div>
        </Form>
    );
}
